<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Typing_and_Validating';
    let nextLink= '/Documentation/Typing_and_Validating/Validator';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="type-provider-typer-1"
  bind:intersecting={visibleById["type-provider-typer-1"]}
  onIntersectChange={(visible) => setVisible("type-provider-typer-1", visible)}
>Type Provider (Typer)</SectionComponent>
<p>Typing determines which elements are valid in specific locations within a model or Abstract Syntax Tree (AST).
For example, in the expression <code>a + 6</code>, it is generally expected that <code>a</code> represents
a numeric value, since other kinds of values would not be valid in this context.</p>
<p>Freon prevents many typing issues by enforcing the use of a typed metamodel. This ensures
that when a certain type, such as a boolean, is required, only a boolean value can be provided. However,
there are scenarios where the metamodel permits a broader range of values than desired, which
can introduce unintended flexibility.</p>
<p>The <strong>type provider</strong> (or <strong>typer</strong>, for short) definition provides the information necessary to
determine the type of element. Any part of the typer
definition must be included in a file with the extension <code>.type</code>. All files with this extension in the <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
><em>defs</em> folder</a>
(i.e. the folder where you keep your definition files) are combined into one typer definition.</p>
<SectionComponent tag="h1" 
  id="typing-rules-2"
  bind:intersecting={visibleById["typing-rules-2"]}
  onIntersectChange={(visible) => setVisible("typing-rules-2", visible)}
>Typing Rules</SectionComponent>
<p>In the typer definition file you can indicate typing rules for every
<em>concept</em> or <em>interface</em> in your language. The typing rules come in four categories. Each category
is there to answer one of the following questions.</p>
<ol>
<li>Which <em>concepts</em> or <em>interfaces</em> are <strong>types</strong>?</li>
<li>Which <em>concepts</em> or <em>interfaces</em> <strong>have a type</strong>?</li>
<li>How to <strong>determine</strong> the type of a <em>concept</em> or <em>interface</em>?</li>
<li>Which types are considered to be <strong>equal or conforming</strong>?</li>
</ol>
<p>Note that these sections need to be in the <code>.type</code> file in this order.</p>
<SectionComponent tag="h2" 
  id="types-or-type-concepts-3"
  bind:intersecting={visibleById["types-or-type-concepts-3"]}
  onIntersectChange={(visible) => setVisible("types-or-type-concepts-3", visible)}
>Types or Type Concepts</SectionComponent>
<p>In Freon all types are completely separate from the elements of your AST. These are called <strong>type concepts</strong>,
which all implement the interface <strong>FreType</strong> from the <code>@freon4dsl/core</code> package. Type concepts can be
defined in two ways. Either they are specified in the <code>.type</code> file, or some AST nodes are declared to
be types. In the latter case, Freon generates a type concept which holds a reference to the AST node.</p>
<p>To indicate which AST concepts are considered to be types in your language,
the keyword <code>isType</code> is used, followed by all types in your language between curly brackets,
and separated by commas.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/typer-docu.type#L4-L4

istype &#123; NamedType &#125;</code></pre>
<p>To define new type concepts you can use a simplified version of the concept definition in the .ast files.
The properties may only be instances of other type concepts, or references to limited concepts. The property
<code>base</code> in the next example is an instance of the interface <code>FreType</code>, whereas the property <code>kind</code> refers to
the limited concept <code>GenericKind</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/typer-docu.type#L6-L9

type GenericType &#123;
    base: FreType;
    kind: GenericKind;
&#125;</code></pre>
<p>Note that it is often good practise to have a common superclass or interface for all your types,
but this is not necessary.</p>
<SectionComponent tag="h2" 
  id="terms-that-have-a-type-4"
  bind:intersecting={visibleById["terms-that-have-a-type-4"]}
  onIntersectChange={(visible) => setVisible("terms-that-have-a-type-4", visible)}
>Terms That Have a Type</SectionComponent>
<p>Not all AST nodes need to have a type associated with them. Only those nodes for which you
need validation, or have another reason to determine a type, need to be marked as
having a type. We refer to those AST nodes as <strong>terms</strong>, so as not to confuse these with
<a
  href="/Documentation/Creating_the_Metamodel/Language_Structure#expression-concept-5"
><code>expression concepts</code></a>,
which have a different meaning.</p>
<p>You can indicate which AST nodes are terms by the keyword <code>hasType</code> followed by all terms in
your language between curly brackets, and separated by commas.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/typer-docu.type#L12-L12

hastype &#123; DocuExpression, DocuType, CalcFunction, Parameter, RiskRef, PayoutRef &#125;</code></pre>
<SectionComponent tag="h2" 
  id="inference-rules-5"
  bind:intersecting={visibleById["inference-rules-5"]}
  onIntersectChange={(visible) => setVisible("inference-rules-5", visible)}
>Inference Rules</SectionComponent>
<p>In order to determine the type of a term, there needs to be a rule, called an <strong>inference rule</strong>,
for each concept or interface that is marked <code>hasType</code>. Each of these rules should result in a type concept.</p>
<p>The following example gives an inference rule that states that the type of calculation function is the
value of its attribute <code>declaredType</code>. This value is an instance of <code>DocuType</code>, which indeed was declared to
represent a type by the above declaration. First, we show the metamodel definition.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L68-L74

concept CalcFunction &#123;
    name: identifier;                   // the name
    description?: Description;          // an optional description
    declaredType : DocuType;            // the type
    body: DocuExpression;               // the actual calculation definition
    parameters: Parameter[];            // any parameters
&#125;</code></pre>
<p>The typer definition looks like this:</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/typer-docu.type#L18-L20

CalcFunction &#123;
    infertype self.declaredType;
&#125;</code></pre>
<Note> 
{#snippet content()}This is a valid inference rule, only if 'declaredType' is an instance 
of a concept that is marked 'isType'.
{/snippet}
</Note>
<p>Concepts that are types (as indicated by <code>isType</code>) can also be terms. If an inference rule
is present, this rule will determine the type of such a term. If no rule is
present, the type of the AST node is the <em>type concept</em> generated for this AST node.</p>
<p>In an inference rule, as in other definition files, it is possible to use
the predefined instances of a <strong>limited concept</strong>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/typer-docu.type#L27-L29

RiskAdjustmentRef &#123;
    infertype #PercentageType:Percentage;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="type-equals-and-type-conformance-rules-6"
  bind:intersecting={visibleById["type-equals-and-type-conformance-rules-6"]}
  onIntersectChange={(visible) => setVisible("type-equals-and-type-conformance-rules-6", visible)}
>Type Equals and Type Conformance Rules</SectionComponent>
<p>The last section in the type definition is where rules are given that state which types
are considered equal or conforming. Each rule can have two entries: the <code>conformsto</code> and
the <code>equalsto</code> entries. Within these entries it is also possible to use the predefined
instances of a limited concept.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/typer-docu.type#L81-L89

// Which types are 'equal' to each other?
NamedType &#123;
    equalsto aa:NamedType where &#123;
            aa.name equalsto self.name;
        &#125;;
    conformsto other:NamedType where &#123;
            other.name equalsto self.name;
        &#125;;
&#125;</code></pre>
<p>The <code>where</code> clause in the above example can be used when a type has some structure. For every property
in the type concept a condition is given. If all conditions evaluate to true then the <code>where</code> clause results in true.</p>
<Note content={content2}> 
{#snippet content2()}
Type concept instances cannot be compared based on their identity,
instead you will need to use an <code>equalsto</code> entry.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="use-of-conformsto-7"
  bind:intersecting={visibleById["use-of-conformsto-7"]}
  onIntersectChange={(visible) => setVisible("use-of-conformsto-7", visible)}
>Use of <strong>conformsto</strong></SectionComponent>
<p>In a condition of a <code>where</code> clause you may use <code>conformsto</code>. In that case, invocation of the rule will produce the cartesian product of
all super concepts of the properties in the conditions. For an
example, see <a
  href="/Examples/Generic_Types"
>Typer Example</a>.</p>
<Note content={content3}>
{#snippet content3()}
Be careful with the use of <code>conformsto</code> within <code>where</code> clauses because this can lead to an 
explosion of newly created type concept instances.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="rules-that-apply-to-any-concept-8"
  bind:intersecting={visibleById["rules-that-apply-to-any-concept-8"]}
  onIntersectChange={(visible) => setVisible("rules-that-apply-to-any-concept-8", visible)}
>Rules That Apply to Any Concept</SectionComponent>
<p>Conformance and other rules can also be defined to apply to <strong>any</strong> concept. This means that
any instance of any concept conforms to the specified value.</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.type#L21-L28

    conformsto #PredefinedType:ANY; // PredefinedType:ANY is the least specific type
&#125;

PredefinedType &#123;
    NULL conformsto anytype; // PredefinedType:NULL is the most specific type
    NUMBER conformsto #PredefinedType:STRING;
&#125;
</code></pre>
<Note content={content4}>
{#snippet content4()}
<code>Anytype</code> rules must be included after the 'hasType' rules and before the inference rules. 
{/snippet}
</Note>


<PrevNextSection {prevLink} {nextLink} />